import React, { useEffect, useState, useCallback } from 'react';
import {
  Crown, CreditCard, Calendar, Clock, CheckCircle2, XCircle,
  AlertTriangle, Smartphone, Receipt, Loader2, RefreshCw
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { subscriptionsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const fmtMoney = (amount, currency = 'xof') => {
  const v = Math.round(Number(amount) || 0).toLocaleString('fr-FR');
  return currency.toLowerCase() === 'xof' ? `${v} FCFA` : `${v} ${currency.toUpperCase()}`;
};

const fmtDate = (s) => {
  if (!s) return '-';
  try { return format(new Date(s), 'dd MMMM yyyy', { locale: fr }); } catch { return s; }
};

const fmtDateTime = (s) => {
  if (!s) return '-';
  try { return format(new Date(s), 'dd/MM/yyyy HH:mm', { locale: fr }); } catch { return s; }
};

const STATUS_BADGES = {
  active: { label: 'Actif', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
  expired: { label: 'Expire', cls: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
  cancelled: { label: 'Annule', cls: 'bg-gray-100 text-gray-800 border-gray-200', icon: XCircle },
  pending: { label: 'En attente', cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock },
};

const PAYMENT_STATUS = {
  initiated: { label: 'Initiee', cls: 'bg-gray-100 text-gray-800' },
  paid: { label: 'Payee', cls: 'bg-emerald-100 text-emerald-800' },
  expired: { label: 'Expiree', cls: 'bg-red-100 text-red-800' },
  failed: { label: 'Echec', cls: 'bg-red-100 text-red-800' },
  pending_review: { label: 'En verification', cls: 'bg-amber-100 text-amber-800' },
};

export default function MySubscription() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await subscriptionsAPI.getMy();
      setData(r.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRenew = () => {
    window.dispatchEvent(new CustomEvent('subscription-expired', {
      detail: { message: 'Renouvelez votre abonnement pour assurer la continuite du service.' },
    }));
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1B3A4B]" />
      </div>
    );
  }

  if (!data || !data.agency) {
    return (
      <div className="p-6 text-center text-gray-500">
        Aucune information d'abonnement disponible.
      </div>
    );
  }

  const agency = data.agency;
  const plans = data.plans || {};
  const currentPlan = plans[agency.subscription_plan] || null;
  const payments = data.payments || [];

  const status = agency.subscription_status || 'pending';
  const Sb = STATUS_BADGES[status] || STATUS_BADGES.pending;
  const SIcon = Sb.icon;

  // Days left calc
  let daysLeft = null;
  let isWarning = false;
  let isExpired = false;
  if (agency.subscription_end_date) {
    const end = new Date(agency.subscription_end_date);
    const now = new Date();
    daysLeft = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    isExpired = daysLeft < 0 || status === 'expired' || status === 'cancelled';
    isWarning = daysLeft >= 0 && daysLeft <= 7 && status === 'active';
  }

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="my-subscription-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#1B3A4B] flex items-center gap-2">
            <Crown className="h-8 w-8 text-[#D4A745]" />
            Mon abonnement
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Statut, plan, historique des paiements de l'agence <span className="font-semibold">{agency.name}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load}
                  data-testid="refresh-sub-btn"
                  className="border-[#1B3A4B] text-[#1B3A4B]">
            <RefreshCw className="h-4 w-4 mr-1" /> Actualiser
          </Button>
          <Button onClick={handleRenew}
                  data-testid="renew-btn"
                  className="bg-[#D4A745] hover:bg-[#b88a30] text-white">
            <CreditCard className="h-4 w-4 mr-2" /> Renouveler maintenant
          </Button>
        </div>
      </div>

      {/* Status overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Statut */}
        <Card><CardContent className="p-5">
          <div className="text-xs uppercase text-gray-500 mb-1">Statut</div>
          <Badge className={`${Sb.cls} border text-base px-3 py-1`}>
            <SIcon className="h-4 w-4 mr-1 inline" />{Sb.label}
          </Badge>
        </CardContent></Card>

        {/* Plan */}
        <Card><CardContent className="p-5">
          <div className="text-xs uppercase text-gray-500 mb-1">Plan</div>
          <div className="text-2xl font-bold text-[#1B3A4B]">
            {currentPlan?.name || agency.subscription_plan || 'Aucun'}
          </div>
          {currentPlan && (
            <div className="text-xs text-gray-500 mt-1">
              {fmtMoney(currentPlan.amount, currentPlan.currency)} / {currentPlan.duration_days}j
            </div>
          )}
        </CardContent></Card>

        {/* Echeance */}
        <Card><CardContent className="p-5">
          <div className="text-xs uppercase text-gray-500 mb-1 flex items-center gap-1">
            <Calendar className="h-3 w-3" /> Echeance
          </div>
          <div className="text-lg font-semibold text-[#1B3A4B]">
            {fmtDate(agency.subscription_end_date)}
          </div>
          {daysLeft !== null && (
            <div className={`text-xs mt-1 font-medium ${
              isExpired ? 'text-red-600' : isWarning ? 'text-[#D4A745]' : 'text-emerald-600'
            }`}>
              {isExpired
                ? `Expire depuis ${Math.abs(daysLeft)} jour(s)`
                : daysLeft === 0 ? "Expire aujourd'hui"
                : daysLeft === 1 ? 'Expire demain'
                : `${daysLeft} jours restants`}
            </div>
          )}
        </CardContent></Card>
      </div>

      {/* Alerte si proche / expire */}
      {(isExpired || isWarning) && (
        <div
          className={`rounded-lg p-4 flex items-start gap-3 border-l-4 ${
            isExpired ? 'bg-red-50 border-red-500' : 'bg-amber-50 border-[#D4A745]'
          }`}
          data-testid="sub-alert"
        >
          <AlertTriangle className={`h-5 w-5 ${isExpired ? 'text-red-600' : 'text-[#D4A745]'} flex-shrink-0 mt-0.5`} />
          <div className="text-sm flex-1">
            <p className="font-semibold text-gray-900">
              {isExpired
                ? 'Votre abonnement est expire'
                : `Votre abonnement expire bientot (${daysLeft} jour${daysLeft > 1 ? 's' : ''})`}
            </p>
            <p className="text-gray-700 mt-1">
              {isExpired
                ? "L'acces a la plateforme est suspendu jusqu'au renouvellement."
                : 'Renouvelez maintenant pour eviter toute interruption de service.'}
            </p>
          </div>
          <Button onClick={handleRenew}
                  className={`flex-shrink-0 ${isExpired ? 'bg-red-600 hover:bg-red-700' : 'bg-[#D4A745] hover:bg-[#b88a30]'} text-white`}>
            <CreditCard className="h-4 w-4 mr-1" /> Renouveler
          </Button>
        </div>
      )}

      {/* Historique paiements */}
      <Card><CardContent className="p-5">
        <h2 className="text-lg font-semibold text-[#1B3A4B] mb-3 flex items-center gap-2">
          <Receipt className="h-5 w-5 text-[#D4A745]" />
          Historique des paiements
        </h2>
        {payments.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            Aucun paiement enregistre pour le moment.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-gray-600 uppercase text-xs">
                  <th className="p-2">Date</th>
                  <th className="p-2">Plan</th>
                  <th className="p-2">Mode</th>
                  <th className="p-2 text-right">Montant</th>
                  <th className="p-2 text-center">Statut</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p, idx) => {
                  const ps = PAYMENT_STATUS[p.payment_status] || { label: p.payment_status, cls: 'bg-gray-100 text-gray-800' };
                  return (
                    <tr key={p.id || idx} className="border-b hover:bg-gray-50" data-testid={`payment-row-${idx}`}>
                      <td className="p-2">{fmtDateTime(p.created_at || p.completed_at)}</td>
                      <td className="p-2 capitalize">{p.plan}</td>
                      <td className="p-2">
                        <span className="inline-flex items-center gap-1 text-xs">
                          {p.payment_method === 'stripe'
                            ? <><CreditCard className="h-3 w-3" /> Stripe</>
                            : <><Smartphone className="h-3 w-3" /> {p.payment_method || 'N/A'}</>}
                        </span>
                      </td>
                      <td className="p-2 text-right font-semibold text-[#1B3A4B]">
                        {fmtMoney(p.amount, p.currency)}
                      </td>
                      <td className="p-2 text-center">
                        <Badge className={`${ps.cls} text-xs`}>{ps.label}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent></Card>

      {/* Info plans disponibles */}
      <Card><CardContent className="p-5">
        <h2 className="text-lg font-semibold text-[#1B3A4B] mb-3">Plans disponibles</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.values(plans).map((p) => (
            <div key={p.id}
                 className={`border rounded-lg p-4 ${p.id === agency.subscription_plan ? 'border-[#D4A745] bg-amber-50/50' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-[#1B3A4B]">{p.name}</h3>
                {p.id === agency.subscription_plan && (
                  <Badge className="bg-[#D4A745] text-white">Plan actuel</Badge>
                )}
              </div>
              <div className="text-2xl font-bold text-[#1B3A4B]">{fmtMoney(p.amount, p.currency)}</div>
              <div className="text-xs text-gray-500 mb-2">/ {p.duration_days} jours</div>
              <p className="text-sm text-gray-600">{p.description}</p>
            </div>
          ))}
        </div>
      </CardContent></Card>

      {/* Footer info */}
      <div className="text-xs text-gray-500 text-center py-2">
        Connecte en tant que <span className="font-semibold">{user?.name}</span> ({user?.role})
      </div>
    </div>
  );
}
